import { FC, memo, useCallback, useEffect, useRef } from "react";
import { cn } from "~/lib/utils";
import type {
  NormalizedRect,
  Point,
  TextSnipState,
} from "../hooks/useTextSnip";

type TextSnipOverlayProps = {
  snipState: TextSnipState;
};

const FILL_COLOR = "rgba(255, 230, 0, 0.18)";
const STROKE_COLOR = "rgba(255, 200, 0, 0.5)";
const PROCESSING_FILL = "rgba(59, 130, 246, 0.2)";
const PROCESSING_STROKE = "rgba(59, 130, 246, 0.7)";
const ERROR_FILL = "rgba(255, 80, 80, 0.15)";
const ERROR_STROKE = "rgba(255, 80, 80, 0.5)";
const STROKE_WIDTH = 2;
const PROCESSING_STROKE_WIDTH = 2.5;
const BORDER_RADIUS = 4;

function getRect(state: TextSnipState): NormalizedRect | null {
  switch (state.status) {
    case "awaitingSecondCorner": {
      const x = Math.min(state.startX, state.currentX);
      const y = Math.min(state.startY, state.currentY);
      const width = Math.abs(state.currentX - state.startX);
      const height = Math.abs(state.currentY - state.startY);
      return { x, y, width, height };
    }
    case "processing":
    case "copying":
    case "error":
    case "empty":
      return state.rect;
    default:
      return null;
  }
}

function getFreehandPoints(state: TextSnipState): Point[] | null {
  switch (state.status) {
    case "freehandDrawing":
      return state.points;
    case "processing":
    case "copying":
    case "error":
    case "empty":
      return state.points ?? null;
    default:
      return null;
  }
}

const TextSnipOverlayComponent: FC<TextSnipOverlayProps> = ({ snipState }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const renderOverlay = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const displayWidth = canvas.clientWidth;
    const displayHeight = canvas.clientHeight;

    if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
      canvas.width = displayWidth;
      canvas.height = displayHeight;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const isError = snipState.status === "error";
    const isProcessing =
      snipState.status === "processing" || snipState.status === "copying";

    let fill = FILL_COLOR;
    let stroke = STROKE_COLOR;
    let lineWidth = STROKE_WIDTH;

    if (isError) {
      fill = ERROR_FILL;
      stroke = ERROR_STROKE;
    } else if (isProcessing) {
      fill = PROCESSING_FILL;
      stroke = PROCESSING_STROKE;
      lineWidth = PROCESSING_STROKE_WIDTH;
    }

    const freehandPoints = getFreehandPoints(snipState);
    if (freehandPoints && freehandPoints.length >= 2) {
      ctx.beginPath();
      ctx.moveTo(
        freehandPoints[0].x * canvas.width,
        freehandPoints[0].y * canvas.height
      );
      for (let i = 1; i < freehandPoints.length; i++) {
        ctx.lineTo(
          freehandPoints[i].x * canvas.width,
          freehandPoints[i].y * canvas.height
        );
      }
      ctx.closePath();
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.strokeStyle = stroke;
      ctx.lineWidth = lineWidth;
      ctx.stroke();
      return;
    }

    const rect = getRect(snipState);
    if (!rect) return;

    const x = rect.x * canvas.width;
    const y = rect.y * canvas.height;
    const w = rect.width * canvas.width;
    const h = rect.height * canvas.height;

    ctx.beginPath();
    if (ctx.roundRect) {
      ctx.roundRect(x, y, w, h, BORDER_RADIUS);
    } else {
      ctx.rect(x, y, w, h);
    }
    ctx.fillStyle = fill;
    ctx.fill();

    ctx.strokeStyle = stroke;
    ctx.lineWidth = lineWidth;
    ctx.stroke();
  }, [snipState]);

  useEffect(() => {
    renderOverlay();
  }, [renderOverlay]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new ResizeObserver(() => {
      renderOverlay();
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [renderOverlay]);

  const isPulsing =
    snipState.status === "processing" || snipState.status === "copying";

  return (
    <canvas
      ref={canvasRef}
      className={cn(
        "absolute inset-0 w-full h-full",
        isPulsing && "animate-snip-pulse"
      )}
      style={{ pointerEvents: "none" }}
    />
  );
};

export const TextSnipOverlay = memo(TextSnipOverlayComponent);
TextSnipOverlay.displayName = "TextSnipOverlay";
